import { cn } from "@veil/ui/lib/utils";
import type { ComponentType, ReactNode, SVGProps } from "react";

/**
 * Presentational helper for the cluster configuration panel. Renders one
 * settings group as a two-column row: a left rail with the group's title,
 * eyebrow icon, and an optional description, and a right column hosting
 * the actual fields. Stacks below md for narrow viewports.
 *
 * Intentionally not a `<Card>` — the parent panel owns the container, so
 * each group is just a hairline-separated row inside it. That keeps us
 * out of the nested-card anti-pattern called out in the impeccable
 * skill's absolute bans.
 */
export function ConfigGroup({
	title,
	icon: Icon,
	description,
	right,
	children,
	className,
}: {
	title: string;
	icon?: ComponentType<SVGProps<SVGSVGElement>>;
	description?: ReactNode;
	right?: ReactNode;
	children: ReactNode;
	className?: string;
}) {
	return (
		<section
			className={cn(
				"grid grid-cols-1 gap-x-10 gap-y-6 px-6 py-7 md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)] md:px-8",
				className,
			)}
		>
			<div className="space-y-1.5">
				<div className="flex items-center gap-2">
					{Icon ? (
						<Icon
							className="size-3.5 text-muted-foreground"
							aria-hidden="true"
						/>
					) : null}
					<h3 className="font-heading text-sm font-medium tracking-tight">
						{title}
					</h3>
				</div>
				{description ? (
					<p className="text-[0.8rem] leading-relaxed text-muted-foreground">
						{description}
					</p>
				) : null}
				{right ? <div className="pt-1">{right}</div> : null}
			</div>
			<div className="min-w-0 space-y-5">{children}</div>
		</section>
	);
}
